import {FC, useRef, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Pause, PlayArrow, Refresh} from '@mui/icons-material';
import {useTtsAudioList, useTtsRegenerateAudio} from '../../hooks/useTts.ts';
import TtsDashboard from './TtsDashboard.tsx';
import {formatKstDateTime} from '../../utils/dateTime.ts';

const AUDIO_LANGS = ['en', 'ko', 'ja', 'zh', 'es', 'fr', 'de'];

const AudioTab: FC = () => {
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [langFilter, setLangFilter] = useState<string>('');
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [playingId, setPlayingId] = useState<number | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const {data: audioData, isLoading} = useTtsAudioList(
        page,
        size,
        langFilter || undefined,
        searchQuery || undefined,
    );

    const regenerate = useTtsRegenerateAudio();

    const rows = audioData?.content ?? [];
    const total = audioData?.totalCount ?? 0;

    const handleSearch = () => {
        setSearchQuery(searchInput);
        setPage(0);
    };

    const handlePlayToggle = (id: number, audioUrl: string) => {
        if (playingId === id) {
            audioRef.current?.pause();
            setPlayingId(null);
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            const audio = new Audio(audioUrl);
            audioRef.current = audio;
            audio.play().catch(() => {});
            audio.onended = () => setPlayingId(null);
            setPlayingId(id);
        }
    };

    const handleRegenerate = (id: number, text: string) => {
        if (!confirm(`"${text.slice(0, 30)}" 항목을 재생성하시겠습니까?`)) return;
        regenerate.mutate({id});
    };

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2, flexWrap: 'wrap', gap: 1}}>
                <FormControl size="small" sx={{minWidth: 120}}>
                    <InputLabel>언어</InputLabel>
                    <Select
                        value={langFilter}
                        label="언어"
                        onChange={e => {
                            setLangFilter(e.target.value);
                            setPage(0);
                        }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        {AUDIO_LANGS.map(l => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    placeholder="텍스트 검색"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') handleSearch();
                    }}
                    sx={{minWidth: 200}}
                />
                <Button variant="outlined" size="small" onClick={handleSearch}>
                    검색
                </Button>
            </Stack>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>텍스트</TableCell>
                        <TableCell>언어</TableCell>
                        <TableCell>Voice</TableCell>
                        <TableCell>파일크기</TableCell>
                        <TableCell>생성일</TableCell>
                        <TableCell>미리듣기</TableCell>
                        <TableCell>재생성</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                <CircularProgress size={24}/>
                            </TableCell>
                        </TableRow>
                    ) : rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                <Typography variant="body2" color="text.secondary">데이터가 없습니다</Typography>
                            </TableCell>
                        </TableRow>
                    ) : rows.map(row => {
                        const isPlaying = playingId === row.id;
                        const fileSizeKb = row.fileSize != null ? (row.fileSize / 1024).toFixed(1) + ' KB' : '-';
                        return (
                            <TableRow key={row.id} hover>
                                <TableCell>{row.id}</TableCell>
                                <TableCell>
                                    <Tooltip title={row.text} placement="top">
                                        <span>{row.text.length > 30 ? row.text.slice(0, 30) + '...' : row.text}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Chip label={row.lang} size="small" variant="outlined"/>
                                </TableCell>
                                <TableCell>{row.voiceName}</TableCell>
                                <TableCell>{fileSizeKb}</TableCell>
                                <TableCell>{formatKstDateTime(row.createdAt)}</TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => handlePlayToggle(row.id, row.audioUrl)}
                                    >
                                        {isPlaying ? <Pause fontSize="small"/> : <PlayArrow fontSize="small"/>}
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRegenerate(row.id, row.text)}
                                        disabled={regenerate.isPending}
                                    >
                                        <Refresh fontSize="small"/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={e => {
                    setSize(parseInt(e.target.value, 10));
                    setPage(0);
                }}
            />
        </Box>
    );
};

const TtsManagementSection: FC = () => {
    const [activeTab, setActiveTab] = useState(0);

    return (
        <Box>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{mb: 3}}>
                <Tab label="대시보드"/>
                <Tab label="생성 완료"/>
            </Tabs>
            {activeTab === 0 && <TtsDashboard/>}
            {activeTab === 1 && <AudioTab/>}
        </Box>
    );
};

export default TtsManagementSection;
